import React from "react";
import * as QRCode from "qrcode";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, QrCode, Logo } from "@/components/icons";
// `?inline` yields a base64 data URI so the composed SVG stays self-contained
// (the downloaded file has no external asset reference to break when printed).
import markDataUrl from "@/assets/logo-mark.png?inline";
import { useCatalog } from "@/app/localization";

/**
 * Branded, printable QR code for a commercial charger's public payment/charging
 * link. Drivers scan it to open `<origin>/charge/<unit>` and pay.
 *
 * The whole card is composed as a single SVG *string* so that the on-screen
 * preview and the downloaded file are byte-identical, and so the entire artwork
 * — VoltiE wordmark + QR modules — stays vector (crisp at any print size). The
 * SVG download is the primary format (scales infinitely); PNG is offered as a
 * convenience for print tools that want a raster.
 *
 * This lives entirely client-side: the link is already known on the page, so no
 * new REST endpoint / response contract is involved.
 */

// VoltiE brand blue (matches --primary / the Logo wordmark).
const BRAND_BLUE = "#38B6FF";

// Card geometry (SVG user units). Aspect is fixed; CSS scales the preview.
const CARD_W = 680;
const CARD_H = 860;
const QR_SIZE = 380;
const QR_X = (CARD_W - QR_SIZE) / 2;
const QR_Y = 210;

// Centered brand-mark overlay. The white plate covers only ~7.5% of the QR
// area — safely inside error-correction level Q's ~25% recovery — and the
// finder patterns (corners) stay clear, so the code still scans.
const QR_CX = QR_X + QR_SIZE / 2;
const QR_CY = QR_Y + QR_SIZE / 2;
const PLATE = 104; // white knockout behind the mark
const MARK_H = 74;
const MARK_W = Math.round(MARK_H * (350 / 512) * 10) / 10; // bolt aspect 350:512

// The VoltiE wordmark, rendered once to a static SVG string so it can be nested
// inside the composed card. Logo aspect is 656:184 (~3.565); width 320 => ~90h.
const LOGO_W = 320;
const LOGO_H = 90;
const LOGO_MARKUP = renderToStaticMarkup(
  <Logo x={(CARD_W - LOGO_W) / 2} y={54} width={LOGO_W} height={LOGO_H} />
);

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Position node-qrcode's `viewBox`-only <svg> as a nested, sized element. */
export function positionQr(qrSvg: string, x: number, y: number, size: number): string {
  return qrSvg.replace(
    /^<svg /,
    `<svg x="${x}" y="${y}" width="${size}" height="${size}" preserveAspectRatio="xMidYMid meet" `
  );
}

/** Printed card text, in the operator's language when the card is generated. */
export interface CardText {
  /** Accessible name of the card image. */
  label: string;
  callToAction: string;
  tagline: string;
}

export function buildCardSvg(qrSvg: string, unitName: string, url: string, text: CardText): string {
  const positionedQr = positionQr(qrSvg, QR_X, QR_Y, QR_SIZE);
  const name = unitName.trim();
  const font = "Nunito, 'Segoe UI', Arial, sans-serif";
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
    `viewBox="0 0 ${CARD_W} ${CARD_H}" ` +
    `width="${CARD_W}" height="${CARD_H}" role="img" aria-label="${escapeXml(text.label)}">` +
    // Card background + brand border.
    `<rect x="6" y="6" width="${CARD_W - 12}" height="${CARD_H - 12}" rx="28" ` +
    `fill="#ffffff" stroke="${BRAND_BLUE}" stroke-width="4"/>` +
    LOGO_MARKUP +
    // Divider under the wordmark.
    `<line x1="140" y1="176" x2="${CARD_W - 140}" y2="176" stroke="#E3E8F1" stroke-width="2"/>` +
    positionedQr +
    // Centered VoltiE bolt on a white knockout plate.
    `<rect x="${QR_CX - PLATE / 2}" y="${QR_CY - PLATE / 2}" width="${PLATE}" height="${PLATE}" ` +
    `rx="18" fill="#ffffff" stroke="#E3E8F1" stroke-width="2"/>` +
    `<image x="${QR_CX - MARK_W / 2}" y="${QR_CY - MARK_H / 2}" width="${MARK_W}" height="${MARK_H}" ` +
    `preserveAspectRatio="xMidYMid meet" href="${markDataUrl}" xlink:href="${markDataUrl}"/>` +
    // Primary call to action.
    `<text x="${CARD_W / 2}" y="656" text-anchor="middle" font-family="${font}" ` +
    `font-size="34" font-weight="700" fill="#0A0A0A">${escapeXml(text.callToAction)}</text>` +
    // Unit name (if any).
    (name
      ? `<text x="${CARD_W / 2}" y="698" text-anchor="middle" font-family="${font}" ` +
        `font-size="22" font-weight="600" fill="${BRAND_BLUE}">${escapeXml(name)}</text>`
      : "") +
    // Human-readable link.
    `<text x="${CARD_W / 2}" y="${name ? 742 : 712}" text-anchor="middle" ` +
    `font-family="${font}" font-size="16" fill="#8A94A6">${escapeXml(url)}</text>` +
    // Footer tagline.
    `<text x="${CARD_W / 2}" y="${CARD_H - 34}" text-anchor="middle" font-family="${font}" ` +
    `font-size="14" fill="#B0B8C4">${escapeXml(text.tagline)}</text>` +
    `</svg>`
  );
}

export function sanitizeFilename(s: string): string {
  const base = (s || "charger").trim().replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "");
  return base || "charger";
}

function triggerDownload(blob: Blob, filename: string) {
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

export function PaymentQrCode({
  url,
  unitName,
}: {
  url: string;
  unitName?: string;
}) {
  const { t, catalog } = useCatalog();
  const [qrSvg, setQrSvg] = React.useState<string | null>(null);
  const [error, setError] = React.useState(false);
  const [pngBusy, setPngBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setError(false);
    setQrSvg(null);
    // Error correction 'Q' (~25% recovery) suits a printed sticker on a
    // physical charger — tolerant of scratches/dirt/fading while still scanning
    // easily at normal sizes.
    QRCode.toString(url, { type: "svg", errorCorrectionLevel: "Q", margin: 2 })
      .then((svg) => {
        if (cancelled) return;
        setQrSvg(svg);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("QR generation failed", e);
        setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  // The card text follows the active language; the QR modules are generated once per link.
  const cardSvg = React.useMemo(
    () =>
      qrSvg
        ? buildCardSvg(qrSvg, unitName ?? "", url, {
            label: t("operator.payment_qr.card_label"),
            callToAction: t("operator.payment_qr.card_call_to_action"),
            tagline: t("operator.payment_qr.card_tagline"),
          })
        : null,
    [qrSvg, unitName, url, t, catalog],
  );

  const filenameBase = `voltie-charge-qr-${sanitizeFilename(unitName ?? "")}`;

  function downloadSvg() {
    if (!cardSvg) return;
    const blob = new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n', cardSvg], {
      type: "image/svg+xml;charset=utf-8",
    });
    triggerDownload(blob, `${filenameBase}.svg`);
    toast.success(t("operator.payment_qr.downloaded", { format: "svg" }));
  }

  async function downloadPng() {
    if (!cardSvg) return;
    setPngBusy(true);
    try {
      // Rasterize the vector card at 3x for a crisp print-ready PNG.
      const scale = 3;
      const dataUrl =
        "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(cardSvg)));
      const img = new Image();
      img.decoding = "sync";
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to render QR image"));
        img.src = dataUrl;
      });
      const canvas = document.createElement("canvas");
      canvas.width = CARD_W * scale;
      canvas.height = CARD_H * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/png")
      );
      if (!blob) throw new Error("Failed to encode PNG");
      triggerDownload(blob, `${filenameBase}.png`);
      toast.success(t("operator.payment_qr.downloaded", { format: "png" }));
    } catch (e: any) {
      console.error(e);
      toast.error(t("operator.payment_qr.png_failed"));
    } finally {
      setPngBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border bg-card p-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <QrCode className="h-4 w-4 text-primary" />
        {t("operator.payment_qr.title")}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t("operator.payment_qr.description")}
      </p>
      <div className="mt-3 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <div className="w-40 shrink-0">
          {error ? (
            <div className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed text-xs text-muted-foreground">
              {t("operator.payment_qr.generate_failed")}
            </div>
          ) : cardSvg ? (
            <div
              className="[&_svg]:h-auto [&_svg]:w-full"
              // Same SVG string that is downloaded — preview and file are identical.
              dangerouslySetInnerHTML={{ __html: cardSvg }}
            />
          ) : (
            <div className="aspect-square w-full animate-pulse rounded-md bg-muted" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            variant="default"
            onClick={downloadSvg}
            disabled={!cardSvg || error}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {t("operator.payment_qr.download", { format: "svg" })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadPng}
            disabled={!cardSvg || error || pngBusy}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {pngBusy ? t("operator.payment_qr.generating") : t("operator.payment_qr.download", { format: "png" })}
          </Button>
          <span className="text-[11px] text-muted-foreground">
            {t("operator.payment_qr.svg_hint")}
          </span>
        </div>
      </div>
    </div>
  );
}
